import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  UserCheck,
  Shield,
  ShieldCheck,
  Plus,
  Edit2,
  Lock,
  Unlock,
  Building2,
  Mail,
  Phone,
  Key,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  Layers,
  UserX
} from 'lucide-react';
import { STAFF_PERMISSIONS, PERMISSION_LABELS, type Permission, type UserRole } from '@vns/core';
import {
  useTeam,
  useCreateStaff,
  useSetStaffActive,
  useUpdateAccess,
  type TeamMember,
  type NewStaff,
} from '../lib/team';
import { useClientOptions } from '../lib/queries';
import { useLookups } from '../lib/lookups';
import { useSession } from '../lib/session';
import Shell from '../components/Shell';
import MultiSelect from '../components/MultiSelect';
import { SectionTitle } from './Suppliers';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  initials,
  useToast,
  Select,
} from '../components/ui';

export default function Team() {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const { data, isLoading, error, refetch } = useTeam();
  const setActive = useSetStaffActive();
  const toast = useToast();
  const me = useSession((s) => s.actor);

  async function toggleActive(m: TeamMember) {
    try {
      await setActive.mutateAsync({ id: m.id, isActive: !m.isActive });
      toast.show(m.isActive ? `${m.fullName} access disabled` : `${m.fullName} reactivated`);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not update access status', true);
    }
  }

  // Summary KPI calculations
  const totalCount = data?.length ?? 0;
  const adminCount = data?.filter((m) => m.role === 'admin').length ?? 0;
  const staffCount = data?.filter((m) => m.role === 'staff').length ?? 0;
  const activeCount = data?.filter((m) => m.isActive).length ?? 0;

  return (
    <Shell
      title="Team & Staff Directory"
      crumb="Site Operations"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Team Member</span>
        </button>
      }
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 18, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Manage user accounts and role-based permissions for site engineers and back-office staff.
          Site staff can only log entries against their assigned projects, subject to admin approval.
        </p>

        {/* Team KPI Metrics */}
        {!isLoading && !!data?.length && (
          <div className="kpi-row" style={{ marginBottom: 20 }}>
            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Total Team</span>
                <div style={{ color: 'var(--brand)', background: 'rgba(37, 99, 235, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Users size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20 }}>{totalCount}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Registered ERP users
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Site Engineers</span>
                <div style={{ color: '#ea580c', background: 'rgba(234, 88, 12, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <Briefcase size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#ea580c' }}>{staffCount}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Field operations staff
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Back Office</span>
                <div style={{ color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <ShieldCheck size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#8b5cf6' }}>{adminCount}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Full administrative access
              </div>
            </div>

            <div className="kpi">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span className="lbl">Active Logins</span>
                <div style={{ color: '#16a34a', background: 'rgba(22, 163, 74, 0.1)', padding: 5, borderRadius: 'var(--radius-xs)' }}>
                  <CheckCircle2 size={14} />
                </div>
              </div>
              <div className="val num" style={{ fontSize: 20, color: '#16a34a' }}>{activeCount}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                Enabled credentials
              </div>
            </div>
          </div>
        )}

        {/* Center-Aligned Team Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            {isLoading ? (
              <TableSkeleton rows={4} cols={6} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => void refetch()} />
            ) : !data?.length ? (
              <EmptyState
                icon="shield"
                title="No team members yet"
                hint="Add site engineers and back-office accounts to grant system access."
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Team Member</th>
                    <th>Role & Designation</th>
                    <th>Assigned Sites</th>
                    <th>Permissions</th>
                    <th>Login Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 12,
                            textAlign: 'left',
                          }}
                        >
                          <div
                            className="avatar-sq"
                            style={{
                              width: 38,
                              height: 38,
                              fontSize: 13,
                              fontWeight: 700,
                              borderRadius: 'var(--radius-sm)',
                              background: m.role === 'admin'
                                ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(37, 99, 235, 0.02))'
                                : 'linear-gradient(135deg, rgba(234, 88, 12, 0.1), rgba(234, 88, 12, 0.02))',
                              color: m.role === 'admin' ? 'var(--brand)' : '#ea580c',
                              border: `1px solid ${m.role === 'admin' ? 'rgba(37, 99, 235, 0.2)' : 'rgba(234, 88, 12, 0.2)'}`,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {initials(m.fullName)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5 }}>
                              {m.fullName}
                            </div>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: 'var(--text-faint)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                marginTop: 2,
                              }}
                            >
                              <Mail size={10} />
                              <span>{m.email}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-xs)',
                              background: m.role === 'admin' ? 'rgba(37, 99, 235, 0.08)' : 'var(--surface-muted)',
                              color: m.role === 'admin' ? 'var(--brand)' : 'var(--text-soft)',
                              border: '1px solid var(--border)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            {m.role === 'admin' ? <Shield size={10} /> : <Briefcase size={10} />}
                            <span>{m.role === 'admin' ? 'Back Office Admin' : 'Site Staff'}</span>
                          </span>
                          {m.designation && (
                            <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4 }}>
                              {m.designation}
                            </div>
                          )}
                        </div>
                      </td>

                      <td style={{ whiteSpace: 'normal', maxWidth: 220 }}>
                        {m.role === 'admin' ? (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-xs)',
                              background: 'rgba(37, 99, 235, 0.08)',
                              color: 'var(--brand)',
                              border: '1px solid rgba(37, 99, 235, 0.2)',
                            }}
                          >
                            All Sites & Ledgers
                          </span>
                        ) : m.projects.length ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                            {m.projects.map((p) => (
                              <span
                                key={p.id}
                                style={{
                                  fontSize: 11,
                                  fontWeight: 500,
                                  padding: '1px 6px',
                                  borderRadius: 'var(--radius-xs)',
                                  background: 'var(--surface-muted)',
                                  border: '1px solid var(--border)',
                                  color: 'var(--text-soft)',
                                }}
                              >
                                {p.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#ea580c', fontSize: 11.5, fontWeight: 500 }}>
                            No sites assigned
                          </span>
                        )}
                      </td>

                      <td className="num">
                        {m.role === 'admin' ? (
                          <span style={{ fontWeight: 600, color: 'var(--text)' }}>Full Access</span>
                        ) : (
                          <span style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>
                            {m.permissions.filter((p) => p !== 'add_client_payment').length} of {STAFF_PERMISSIONS.length} modules
                          </span>
                        )}
                      </td>

                      <td>
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: '3px 9px',
                            borderRadius: 'var(--radius-xs)',
                            background: m.isActive ? 'rgba(22, 163, 74, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: m.isActive ? '#16a34a' : '#dc2626',
                            border: `1px solid ${m.isActive ? 'rgba(22, 163, 74, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          {m.isActive ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                          <span>{m.isActive ? 'Active' : 'Disabled'}</span>
                        </span>
                      </td>

                      <td>
                        {m.role === 'staff' ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button
                              className="btn btn-auto btn-sm"
                              onClick={() => setEditing(m)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12 }}
                            >
                              <Edit2 size={12} />
                              <span>Access</span>
                            </button>
                            {m.id !== me?.id && (
                              <button
                                className="btn btn-auto btn-sm btn-ghost"
                                onClick={() => void toggleActive(m)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  color: m.isActive ? '#dc2626' : '#16a34a',
                                }}
                              >
                                {m.isActive ? <Lock size={12} /> : <Unlock size={12} />}
                                <span>{m.isActive ? 'Disable' : 'Enable'}</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>Admin Account</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </motion.div>

      {adding && <AddStaffDrawer onClose={() => setAdding(false)} />}
      {editing && <EditAccessDrawer member={editing} onClose={() => setEditing(null)} />}
    </Shell>
  );
}

/* ------------------------------ Permission Picker ------------------------------ */

function PermissionPicker({
  selected,
  onChange,
}: {
  selected: Permission[];
  onChange: (p: Permission[]) => void;
}) {
  const toggle = (p: Permission) =>
    onChange(selected.includes(p) ? selected.filter((x) => x !== p) : [...selected, p]);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {STAFF_PERMISSIONS.map((p) => {
        const on = selected.includes(p);
        return (
          <label
            key={p}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              cursor: 'pointer',
              padding: '10px 14px',
              borderRadius: 'var(--radius-sm)',
              border: on ? '1px solid rgba(37, 99, 235, 0.3)' : '1px solid var(--border)',
              background: on ? 'rgba(37, 99, 235, 0.04)' : 'var(--surface)',
              transition: 'all 0.15s ease',
            }}
          >
            <input
              type="checkbox"
              checked={on}
              onChange={() => toggle(p)}
              style={{
                width: 16,
                height: 16,
                marginTop: 2,
                flexShrink: 0,
                accentColor: 'var(--brand)',
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                {PERMISSION_LABELS[p].label}
              </span>
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-soft)', marginTop: 2 }}>
                {PERMISSION_LABELS[p].description}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

/* ------------------------------- Add Staff Drawer ------------------------------- */

const EMPTY: NewStaff = {
  full_name: '',
  email: '',
  password: '',
  phone: '',
  designation: '',
  role: 'staff',
  project_ids: [],
  permissions: [],
};

function AddStaffDrawer({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<NewStaff>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: lk } = useLookups();
  const create = useCreateStaff();
  const toast = useToast();

  const set = (k: keyof NewStaff) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.full_name.trim()) next.full_name = 'Full name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email address';
    if (form.password.length < 8) next.password = 'Password must be at least 8 characters';
    if (form.role === 'staff' && !form.project_ids.length) next.project_ids = 'Assign at least one construction site';
    if (form.role === 'staff' && !form.permissions.length) next.permissions = 'Select at least one permitted module';
    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      await create.mutateAsync(form);
      toast.show(`${form.full_name} registered successfully`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not create the account', true);
    }
  }

  const isStaff = form.role === 'staff';

  return (
    <Drawer
      title="Add Team Member"
      description="Create login credentials for site engineers or office staff"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Create User Login"
      busy={create.isPending}
    >
      <div className="form-grid">
        <Field label="Full Name" error={errors.full_name} span2>
          <input
            value={form.full_name}
            onChange={set('full_name')}
            autoFocus
            aria-invalid={!!errors.full_name}
          />
        </Field>

        <Field
          label="Email Address (Login ID)"
          error={errors.email}
          span2
          hint={!errors.email ? 'Will be used to sign in to the ERP application' : undefined}
        >
          <input
            type="email"
            value={form.email}
            onChange={set('email')}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field
          label="Initial Password"
          error={errors.password}
          hint={!errors.password ? 'Minimum 8 characters' : undefined}
        >
          <input
            type="text"
            value={form.password}
            onChange={set('password')}
            aria-invalid={!!errors.password}
          />
        </Field>

        <Field label="Phone Number" optional>
          <input
            value={form.phone}
            onChange={set('phone')}
            inputMode="tel"
          />
        </Field>

        <Field label="Account Role">
          <Select
            value={form.role}
            onChange={(val) =>
              setForm((f) => ({
                ...f,
                role: val as UserRole,
              }))
            }
            options={[
              { value: 'staff', label: 'Site Staff — limited to assigned projects' },
              { value: 'admin', label: 'Back Office Admin — full system access' },
            ]}
          />
        </Field>

        <Field label="Designation / Title" optional>
          <input
            value={form.designation}
            onChange={set('designation')}
          />
        </Field>
      </div>

      {isStaff ? (
        <>
          <SectionTitle>Assigned Project Sites</SectionTitle>
          {errors.project_ids && (
            <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8, fontWeight: 500 }}>
              {errors.project_ids}
            </div>
          )}
          <MultiSelect
            options={lk?.projects}
            selected={form.project_ids}
            onChange={(ids) => setForm((f) => ({ ...f, project_ids: ids }))}
            emptyHint="No active projects available."
            collapseAfter={10}
          />

          <SectionTitle>Permitted Modules & Entry Types</SectionTitle>
          {errors.permissions && (
            <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8, fontWeight: 500 }}>
              {errors.permissions}
            </div>
          )}
          <PermissionPicker
            selected={form.permissions}
            onChange={(p) => setForm((f) => ({ ...f, permissions: p }))}
          />
        </>
      ) : (
        <div
          style={{
            marginTop: 18,
            padding: '12px 16px',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(37, 99, 235, 0.06)',
            color: 'var(--brand)',
            fontSize: 12.5,
            border: '1px solid rgba(37, 99, 235, 0.15)',
          }}
        >
          <strong>Back-Office Admin Account:</strong> Grants unrestricted access to all project sites, financial ledgers, vendor accounts, and approvals.
        </div>
      )}
    </Drawer>
  );
}

/* ------------------------------ Edit Access Drawer ------------------------------ */

function EditAccessDrawer({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const me = useSession((s) => s.actor);
  const [projectIds, setProjectIds] = useState<string[]>(member.projects.map((p) => p.id));
  const [permissions, setPermissions] = useState<Permission[]>(member.permissions);
  const { data: lk } = useLookups();
  const update = useUpdateAccess();
  const toast = useToast();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!me) return;
    try {
      await update.mutateAsync({
        profileId: member.id,
        companyId: me.companyId,
        projectIds,
        permissions,
      });
      toast.show(`Access permissions updated for ${member.fullName}`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not update access permissions', true);
    }
  }

  return (
    <Drawer
      title="Edit Staff Access"
      description={member.fullName}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Access Permissions"
      busy={update.isPending}
    >
      <SectionTitle>Assigned Project Sites</SectionTitle>
      <MultiSelect
        options={lk?.projects}
        selected={projectIds}
        onChange={setProjectIds}
        collapseAfter={10}
      />

      <SectionTitle>Permitted Modules</SectionTitle>
      <PermissionPicker selected={permissions} onChange={setPermissions} />

      {(!projectIds.length || !permissions.length) && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(234, 88, 12, 0.08)',
            color: '#ea580c',
            fontSize: 12.5,
            border: '1px solid rgba(234, 88, 12, 0.2)',
          }}
        >
          Notice: Without assigned project sites or module permissions, {member.fullName.split(' ')[0]} can sign in but cannot submit any site entries.
        </div>
      )}
    </Drawer>
  );
}
